import { MessageDetail, MessageDetails } from '../../message-detail.model';
import { LogService } from '../../../log.service';
import { Subject } from "rxjs";

/**
 *  Detail progress state 
 */
export enum ProgressDetailState {
    Waiting,    // waiting to be processed
    InProgress, // in progress
    Done,       // succeeded
    Canceled,   // canceled
    Error       // error
};

/**
 *  Detail progress text 
 */
export const ProgressStateText: string[] = [
    "Waiting",
    "In Progress",
    "Done",
    "Canceled",
    "Error"
];

/**
 *  Detail progress type icons 
 */
export const ProgressDetailTypeIcons: string[] = [
    "assets/images/dismiss24.svg",              // Waiting
    "",                                         // InProgress - no icon - use dots
    "assets/images/status_complete24.svg",      // Done
    "",                                         // Canceled - no icon - just text
    "assets/images/formfield_error.svg",        // Error
];

export class ProgressMessageDetail extends MessageDetail {
    state: ProgressDetailState = ProgressDetailState.Waiting;
    percentComplete: number = 0;

    cancelRequested: boolean = false;
    canceled = new Subject<string>();

    constructor(uniqueId: string, message: string, icon: string, state: ProgressDetailState) {
        super(uniqueId, message, icon);
        this.state = state;
        this.statusText = ProgressDetailState[state];
    }

    stop() {
        this.cancelRequested = true;
        this.state = ProgressDetailState.Canceled;
        this.statusText = ProgressStateText[ProgressDetailState.Canceled];
        this.canceled.next(this.uniqueId);
    }
}

export class ProgressMessageDetails extends MessageDetails {
    details: ProgressMessageDetail[] = [];
    stopRequested: boolean = false;
    stopped = new Subject<boolean>();
    errors: number = 0;
    canceled: number = 0;
    complete: number = 0;

    constructor(private logService: LogService, apiInprogress: Subject<string>) {
        super();
        apiInprogress.subscribe(uniqueId => {
            this.logService.logMessage(`ProgressMessageDetails:apiInprogress enter : ${uniqueId}`);
            const dd = this.find(uniqueId);
            if (dd) {
                dd.state = ProgressDetailState.InProgress;
            }
        });
    }

    /**
     * Add an item
     * @param detail detail to add
     */
    addItem(detail: ProgressMessageDetail) {
        this.logService.logMessage(`ProgressMessageDetails:addItem enter : ${detail.uniqueId}`);
        this.details.push(detail);

        // item may already be canceled
        if (detail.state == ProgressDetailState.Canceled) {
            this.canceled++;
        }

        detail.canceled.subscribe(() => {
            this.canceled++;
        });
    }

    /**
     * Find a detail entry based on its unique identifier
     * @param uniqueId Unique index to find
     * @return Detail item 
     */
    find(uniqueId: string) {
        return this.details.find(d => d.uniqueId == uniqueId);
    }

    /**
     * Stop all waiting to be processed
     */
    stopAll() {
        this.logService.logMessage('ProgressMessageDetails:stopAll enter.');
        this.details.forEach(dd => {
            if (dd.state == ProgressDetailState.Waiting || dd.state == ProgressDetailState.InProgress) {
                this.logService.logMessage('ProgressMessageDetails:stopAll : Canceling : ' + dd.uniqueId);
                dd.stop();
            }
        });

        this.stopRequested = true;
    }

    filter(uniqueId: string): boolean {
        const dd = this.find(uniqueId);
        if (dd) {
            return dd.state == ProgressDetailState.Waiting || dd.state == ProgressDetailState.InProgress;
        }
        return false;
    }

    get isComplete(): boolean {
        return this.details.findIndex(d => d.state == ProgressDetailState.Waiting || d.state == ProgressDetailState.InProgress) < 0;
    }

    markAsComplete(uniqueId: string) {
        const detail = this.find(uniqueId);
        if (detail) {
            this.complete++;
            detail.state = ProgressDetailState.Done;
            detail.statusText = ProgressDetailState[ProgressDetailState.Done];
        }
        this.emitStop();
    }

    markAsError(uniqueId: string, errorMsg: string) {
        this.logService.logMessage(errorMsg);

        const detail = this.find(uniqueId);
        if (detail) {
            this.errors++;
            detail.state = ProgressDetailState.Error;
            detail.statusText = errorMsg;
        }

        this.emitStop();
    }

    private emitStop() {
        if (this.stopRequested) {
            this.logService.logMessage('ProgressMessageDetails; emitting stop');
            this.stopped.next(true);
        }
    }

    get percentComplete() {
        // Prevent divide by zero
        if (this.details.length > 0) {
            let totalPercentages = 0;
            this.details.forEach(d => {
                totalPercentages += d.percentComplete;
            });

            return totalPercentages / this.details.length;
        }
        return 0;
    }
}